'use client';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { portalContainerId } from '@/components/Portal/PortalContainer';
import classNames from 'classnames';
import Icon from '@/components/Icon/Icon';

type TModalProps = {
    isOpen: boolean;
    handleClose: () => void;
    children: ReactNode;
    withPadding?: boolean;
    classes?: {
        root?: string;
        container?: string;
    };
};

const SMALL_SCREEN_WIDTH = 575;

export const Modal = ({
    isOpen,
    handleClose,
    withPadding,
    children,
    classes = { root: '', container: '' },
}: TModalProps) => {
    const modalContainerRef = useRef<Element | null>(null);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const [isVisible, setIsVisible] = useState(isOpen);
    const [isMounted, setIsMounted] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const updateMobileView = useCallback(() => {
        const width = window.innerWidth;
        if (width <= SMALL_SCREEN_WIDTH) {
            setIsMobile(true);
        } else {
            setIsMobile(false);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('resize', updateMobileView);

        updateMobileView();
        return () => window.removeEventListener('resize', updateMobileView);
    }, [updateMobileView]);

    // Handle animation on open/close
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'initial';
            // Match duration with CSS animation duration
            const timeoutId = setTimeout(() => setIsVisible(false), 300);
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen]);

    // Handle modal display on page open
    useEffect(() => {
        if (isOpen && isMounted && !isVisible) setIsVisible(true);
    }, [isMounted, isOpen, isVisible]);

    // Handle close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isVisible && modalRef.current && !modalRef.current.contains(event.target as Node)) {
                handleClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClose, isVisible]);

    // Set the portal target to ref to avoid hydration mismatch
    useEffect(() => {
        modalContainerRef.current = document.getElementById(portalContainerId);

        setIsMounted(true);
    }, []);

    if (!isVisible || typeof window === 'undefined' || !modalContainerRef.current) {
        return null;
    }

    return createPortal(
        <div
            className={classNames(
                'tw-bg-black tw-bg-opacity-50 tw-flex tw-overflow-y-auto tw-overflow-x-hidden tw-fixed tw-top-0 tw-right-0 tw-left-0 tw-z-[200] tw-justify-center tw-h-full tw-items-center tw-w-full tw-max-h-full',
                {
                    'tw-animate-modal-fade-in': isOpen,
                    'tw-animate-modal-fade-out': !isOpen,
                },
            )}>
            <div
                ref={modalRef}
                className={classNames(
                    'min-[500px]:tw-rounded-lg tw-max-h-full  md:tw-max-w-[730px] lg:tw-max-w-[1080px] lg:tw-w-[90%]  tw-min-h-full tw-bg-white',
                    {
                        'tw-animate-modal-in': isOpen,
                        'tw-animate-modal-out': !isOpen,
                    },
                    classes.root,
                )}>
                <div
                    className={classNames(
                        'tw-relative tw-rounded-lg  tw-h-full  lg:tw-p-[40px] tw-min-w-[200px]',
                        {
                            'tw-p-0': isMobile,
                            'tw-p-[25px] tw-pt-[35px]': isMobile && withPadding,
                            'tw-p-[25px]': !isMobile,
                        },
                        classes.container,
                    )}>
                    {isMobile ? (
                        <div
                            className="tw-cursor-pointer
                        tw-absolute
                        tw-right-3
                        tw-top-2
                        tw-w-[30px]
                        tw-h-[30px]
                        tw-bg-gray-100
                        tw-opacity-80
                        tw-rounded-full
                        ">
                            <Icon
                                type="close"
                                width={30}
                                height={30}
                                onClick={handleClose}
                                className="tw-text-3xl"
                            />
                        </div>
                    ) : (
                        <Icon
                            type="close"
                            width={30}
                            height={30}
                            onClick={handleClose}
                            className="tw-cursor-pointer tw-absolute tw-right-4 tw-top-4 tw-text-3xl"
                        />
                    )}
                    {children}
                </div>
            </div>
        </div>,
        modalContainerRef.current,
    );
};
